import { useEffect, useState, lazy, Suspense } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { mediaUrl, IMG } from '../lib/mediaUrl';
import OptionWheel from '../components/reactbits/OptionWheel';
import { prefersReducedMotion } from '../lib/motionPrefs';

// Design-exploration page, not linked from anywhere. Three full homepage
// treatments behind one URL so Chase can flip between them and pick:
//   /templates?t=1  Daybreak   — light theme, Lightfall hero
//   /templates?t=2  Showroom   — vault photo hero, Circular Gallery section
//   /templates?t=3  Nebula     — dark, Antigravity particle hero
// Every template also carries the OptionWheel menu overlay. No gold anywhere
// by explicit request; each template has its own palette + font.
const Lightfall = lazy(() => import('../components/reactbits/Lightfall'));
const CircularGallery = lazy(() => import('../components/reactbits/CircularGallery'));
const Antigravity = lazy(() => import('../components/reactbits/Antigravity'));

function money(cents) {
  return `$${(cents / 100).toFixed(2)}`;
}

const MENU_ITEMS = ['Home', 'Shop', 'New Arrivals', 'Track Order', 'Cart', 'Account'];

function MenuOverlay({ open, onClose, bg, textColor, activeColor, font }) {
  if (!open) return null;
  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 90, background: bg,
        backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
        fontFamily: font,
      }}
    >
      <button
        onClick={onClose}
        style={{
          position: 'absolute', top: 24, right: 28, zIndex: 91, background: 'none',
          color: textColor, fontSize: '0.9rem', letterSpacing: '0.12em',
          textTransform: 'uppercase', cursor: 'pointer', padding: 8,
        }}
      >
        Close
      </button>
      <div style={{ position: 'absolute', inset: 0 }}>
        <OptionWheel
          items={MENU_ITEMS}
          defaultSelected={1}
          textColor={textColor}
          activeColor={activeColor}
          side="right"
          fontSize={2.6}
          spacing={1.5}
          curve={1}
          tilt={7}
          blur={1.5}
          fade={0.22}
          smoothing={200}
          inset={90}
          draggable
        />
      </div>
      <div
        style={{
          position: 'absolute', bottom: 32, right: 90, color: textColor, opacity: 0.5,
          fontSize: '0.78rem', letterSpacing: '0.08em',
        }}
      >
        Scroll, drag, or use arrow keys
      </div>
    </div>
  );
}

function TemplateNav({ ink, accent, font, onMenu }) {
  return (
    <div
      style={{
        position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '22px 34px', fontFamily: font,
      }}
    >
      <div style={{ fontWeight: 800, fontSize: '1.05rem', letterSpacing: '0.02em', color: ink }}>
        SHINY VAULT <span style={{ color: accent }}>LGS</span>
      </div>
      <button
        onClick={onMenu}
        style={{
          background: 'none', color: ink, fontSize: '0.85rem', letterSpacing: '0.14em',
          textTransform: 'uppercase', cursor: 'pointer', padding: 8, fontFamily: font,
        }}
      >
        Menu
      </button>
    </div>
  );
}

function ProductStrip({ products, cardBg, ink, sub, accent, font, shadow }) {
  return (
    <div
      style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: 18, maxWidth: 1100, margin: '0 auto', padding: '0 34px',
      }}
    >
      {products.slice(0, 4).map((p) => (
        <div
          key={p.id}
          style={{
            background: cardBg, borderRadius: 14, overflow: 'hidden',
            boxShadow: shadow, fontFamily: font,
          }}
        >
          <div style={{ aspectRatio: '1', background: 'rgba(127,127,127,0.08)' }}>
            {p.photoUrl && (
              <img src={p.photoUrl} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            )}
          </div>
          <div style={{ padding: '12px 14px 16px' }}>
            <div style={{ fontWeight: 600, fontSize: '0.85rem', color: ink, marginBottom: 4 }}>{p.name}</div>
            <div style={{ fontWeight: 700, fontSize: '0.9rem', color: accent }}>{money(p.price_cents)}</div>
          </div>
        </div>
      ))}
      {products.length === 0 && (
        <div style={{ color: sub, fontFamily: font, gridColumn: '1 / -1', textAlign: 'center' }}>
          Product cards render here once listings are live.
        </div>
      )}
    </div>
  );
}

// ---------- Template 1: Daybreak (light, Lightfall) ----------
function TemplateDaybreak({ products, menuOpen, setMenuOpen }) {
  const font = "'Sora', sans-serif";
  const ink = '#191724';
  const sub = '#5d5a6e';
  const accent = '#5227ff';
  // Lightfall's shader is additive over black — it has no real light mode. So
  // Daybreak is a light page with the Lightfall hero as a contained dark
  // feature band: white text on the canvas, paper-light everything below.
  return (
    <div style={{ background: '#f6f4ef', minHeight: '100vh', fontFamily: font }}>
      <section style={{ position: 'relative', height: '80vh', overflow: 'hidden', borderRadius: '0 0 32px 32px' }}>
        {!prefersReducedMotion() && (
          <Suspense fallback={null}>
            <div style={{ position: 'absolute', inset: 0 }}>
              <Lightfall
                colors={['#a996ff', '#9fb8ff', '#ffb3ec']}
                backgroundColor="#1c1240"
                speed={0.35}
                streakCount={3}
                glow={0.6}
                density={0.5}
                twinkle={0.6}
                zoom={3}
                backgroundGlow={0.3}
                mouseInteraction
                mouseStrength={0.4}
              />
            </div>
          </Suspense>
        )}
        <TemplateNav ink="#f4f2ff" accent="#c3b5ff" font={font} onMenu={() => setMenuOpen(true)} />
        <div
          style={{
            position: 'relative', zIndex: 5, maxWidth: 1100, margin: '0 auto',
            padding: '24vh 34px 0',
          }}
        >
          <div style={{ fontSize: '0.8rem', letterSpacing: '0.22em', textTransform: 'uppercase', color: '#c3b5ff', fontWeight: 600, marginBottom: 18 }}>
            New arrivals weekly
          </div>
          <h1 style={{ fontSize: 'clamp(2.6rem, 6vw, 4.6rem)', fontWeight: 800, color: '#ffffff', lineHeight: 1.04, letterSpacing: '-0.03em', margin: 0, maxWidth: 640 }}>
            The vault is open.
          </h1>
          <p style={{ color: '#cfc9e8', maxWidth: 460, fontSize: '1.02rem', lineHeight: 1.6, margin: '20px 0 30px' }}>
            Graded slabs, sealed product, and vintage singles — sourced, authenticated, and shipped from one collection to the next.
          </p>
          <button
            style={{
              background: accent, color: '#fff', fontFamily: font, fontWeight: 700,
              fontSize: '0.95rem', padding: '15px 32px', borderRadius: 999, cursor: 'pointer',
              boxShadow: '0 12px 30px rgba(82,39,255,0.28)',
            }}
          >
            Enter the shop
          </button>
        </div>
      </section>
      {/* Chase 2026-07-19: Daybreak is the pick, with Template 3's Antigravity
          field pulled in as the full shopping background behind the cards,
          plus extra breathing room after the hero band. */}
      <section style={{ position: 'relative', padding: '130px 0 150px', minHeight: '70vh', overflow: 'hidden' }}>
        {!prefersReducedMotion() && (
          <Suspense fallback={null}>
            <div style={{ position: 'absolute', inset: 0, zIndex: 0 }}>
              <Antigravity
                count={260}
                color={accent}
                particleSize={1.3}
                magnetRadius={9}
                waveSpeed={0.25}
                waveAmplitude={0.7}
                lerpSpeed={0.08}
                particleShape="capsule"
                fieldStrength={8}
              />
            </div>
          </Suspense>
        )}
        <div style={{ position: 'relative', zIndex: 1 }}>
          <h2 style={{ textAlign: 'center', color: ink, fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 40 }}>New arrivals</h2>
          <ProductStrip
            products={products}
            cardBg="#ffffff"
            ink={ink}
            sub={sub}
            accent={accent}
            font={font}
            shadow="0 10px 30px rgba(25,23,36,0.08)"
          />
        </div>
      </section>
      <MenuOverlay
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        bg="rgba(246,244,239,0.93)"
        textColor="#a5a1b4"
        activeColor={accent}
        font={font}
      />
    </div>
  );
}

// ---------- Template 2: Showroom (vault photo, Circular Gallery) ----------
function TemplateShowroom({ products, menuOpen, setMenuOpen }) {
  const font = "'Space Grotesk', sans-serif";
  const ink = '#eef2f8';
  const sub = '#93a1b8';
  const accent = '#2dd4bf';
  const galleryItems = (products.length ? products : []).filter((p) => p.photoUrl).map((p) => ({ image: p.photoUrl, text: p.name.replace('SEED — ', '') }));
  const items = galleryItems.length >= 4 ? galleryItems : [...galleryItems, ...galleryItems, ...galleryItems].slice(0, 6);
  return (
    <div style={{ background: '#0e1420', minHeight: '100vh', fontFamily: font }}>
      <section style={{ position: 'relative', height: '78vh', overflow: 'hidden' }}>
        <div
          style={{
            position: 'absolute', inset: 0, backgroundImage: "url('/hero-vault.jpg')",
            backgroundSize: 'cover', backgroundPosition: 'center 30%',
          }}
        />
        <div
          style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(180deg, rgba(14,20,32,0.55) 0%, rgba(14,20,32,0.82) 70%, #0e1420 100%)',
          }}
        />
        <TemplateNav ink={ink} accent={accent} font={font} onMenu={() => setMenuOpen(true)} />
        <div style={{ position: 'relative', zIndex: 5, maxWidth: 1100, margin: '0 auto', padding: '30vh 34px 0' }}>
          <h1 style={{ fontSize: 'clamp(2.4rem, 5.5vw, 4.2rem)', fontWeight: 700, color: ink, lineHeight: 1.05, letterSpacing: '-0.02em', margin: 0, maxWidth: 620 }}>
            The vault is <span style={{ color: accent }}>open.</span>
          </h1>
          <p style={{ color: sub, maxWidth: 440, fontSize: '1rem', lineHeight: 1.6, margin: '18px 0 28px' }}>
            Graded slabs, sealed product, and vintage singles — authenticated and shipped with care.
          </p>
          <button
            style={{
              background: 'transparent', border: `1.5px solid ${accent}`, color: accent,
              fontFamily: font, fontWeight: 700, fontSize: '0.95rem', padding: '14px 30px',
              borderRadius: 4, cursor: 'pointer', letterSpacing: '0.04em',
            }}
          >
            ENTER THE SHOP
          </button>
        </div>
      </section>
      <section style={{ padding: '60px 0 40px' }}>
        <h2 style={{ textAlign: 'center', color: ink, fontWeight: 700, marginBottom: 8 }}>The showcase</h2>
        <div style={{ textAlign: 'center', color: sub, fontSize: '0.9rem', marginBottom: 10 }}>Drag to browse</div>
        <div style={{ height: 420, position: 'relative' }}>
          {items.length > 0 && (
            <Suspense fallback={null}>
              <CircularGallery items={items} bend={2.2} textColor={ink} borderRadius={0.06} font={`bold 26px Space Grotesk`} scrollSpeed={2} scrollEase={0.06} />
            </Suspense>
          )}
        </div>
      </section>
      <section style={{ padding: '30px 0 90px' }}>
        <ProductStrip
          products={products}
          cardBg="#161e2e"
          ink={ink}
          sub={sub}
          accent={accent}
          font={font}
          shadow="0 10px 30px rgba(0,0,0,0.35)"
        />
      </section>
      <MenuOverlay
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        bg="rgba(14,20,32,0.93)"
        textColor="#5a6a82"
        activeColor={accent}
        font={font}
      />
    </div>
  );
}

// ---------- Template 3: Nebula (dark, Antigravity) ----------
function TemplateNebula({ products, menuOpen, setMenuOpen }) {
  const font = "'Outfit', sans-serif";
  const ink = '#f4f2ff';
  const sub = '#9d97b8';
  const accent = '#22d3ee';
  const accent2 = '#7c5cff';
  return (
    <div style={{ background: '#0a0912', minHeight: '100vh', fontFamily: font }}>
      <section style={{ position: 'relative', height: '86vh', overflow: 'hidden' }}>
        {!prefersReducedMotion() && (
          <Suspense fallback={null}>
            <div style={{ position: 'absolute', inset: 0 }}>
              <Antigravity
                count={340}
                color={accent2}
                particleSize={1.6}
                magnetRadius={9}
                waveSpeed={0.3}
                waveAmplitude={0.8}
                lerpSpeed={0.08}
                particleShape="capsule"
                fieldStrength={9}
              />
            </div>
          </Suspense>
        )}
        <TemplateNav ink={ink} accent={accent} font={font} onMenu={() => setMenuOpen(true)} />
        <div style={{ position: 'relative', zIndex: 5, textAlign: 'center', padding: '30vh 34px 0', pointerEvents: 'none' }}>
          <h1
            style={{
              fontSize: 'clamp(2.8rem, 7vw, 5.2rem)', fontWeight: 800, lineHeight: 1.02,
              letterSpacing: '-0.03em', margin: 0,
              background: `linear-gradient(92deg, ${accent2}, ${accent})`,
              WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}
          >
            The vault is open.
          </h1>
          <p style={{ color: sub, maxWidth: 460, fontSize: '1.02rem', lineHeight: 1.6, margin: '22px auto 32px' }}>
            Graded slabs, sealed product, and vintage singles — move your cursor through the field.
          </p>
          <button
            style={{
              background: `linear-gradient(92deg, ${accent2}, ${accent})`, color: '#0a0912',
              fontFamily: font, fontWeight: 800, fontSize: '0.95rem', padding: '15px 34px',
              borderRadius: 999, cursor: 'pointer', pointerEvents: 'auto',
            }}
          >
            Enter the shop
          </button>
        </div>
      </section>
      <section style={{ padding: '70px 0 90px' }}>
        <h2 style={{ textAlign: 'center', color: ink, fontWeight: 700, marginBottom: 36 }}>New arrivals</h2>
        <ProductStrip
          products={products}
          cardBg="#131120"
          ink={ink}
          sub={sub}
          accent={accent}
          font={font}
          shadow="0 10px 34px rgba(124,92,255,0.14)"
        />
      </section>
      <MenuOverlay
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        bg="rgba(10,9,18,0.93)"
        textColor="#544f6e"
        activeColor={accent}
        font={font}
      />
    </div>
  );
}

const TEMPLATES = [
  { id: 1, name: 'Daybreak', component: TemplateDaybreak },
  { id: 2, name: 'Showroom', component: TemplateShowroom },
  { id: 3, name: 'Nebula', component: TemplateNebula },
];

export default function TemplatesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const active = Math.min(3, Math.max(1, parseInt(searchParams.get('t'), 10) || 1));
  const [products, setProducts] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    supabase
      .from('products')
      .select('id, name, price_cents, product_media(storage_path, sort_order)')
      .eq('status', 'active')
      .order('created_at', { ascending: false })
      .limit(8)
      .then(({ data }) => {
        setProducts(
          (data || []).map((p) => {
            const photos = (p.product_media || []).slice().sort((a, b) => a.sort_order - b.sort_order);
            return {
              ...p,
              photoUrl: photos[0]
                ? mediaUrl(photos[0].storage_path, IMG.card)
                : null,
            };
          })
        );
      });
  }, []);

  const Active = TEMPLATES[active - 1].component;

  return (
    <div>
      <Active products={products} menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
      <div
        style={{
          position: 'fixed', bottom: 18, left: '50%', transform: 'translateX(-50%)', zIndex: 100,
          display: 'flex', gap: 6, background: 'rgba(20,20,28,0.85)', backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)', borderRadius: 999, padding: 6,
          boxShadow: '0 8px 30px rgba(0,0,0,0.4)',
        }}
      >
        {TEMPLATES.map((t) => (
          <button
            key={t.id}
            onClick={() => {
              setMenuOpen(false);
              setSearchParams({ t: String(t.id) });
            }}
            style={{
              background: t.id === active ? '#ffffff' : 'transparent',
              color: t.id === active ? '#14141c' : '#b9b6c9',
              fontWeight: 700, fontSize: '0.8rem', padding: '9px 18px', borderRadius: 999,
              cursor: 'pointer', letterSpacing: '0.03em',
            }}
          >
            {t.id}. {t.name}
          </button>
        ))}
      </div>
    </div>
  );
}
